// A session row's menu (••• , right-click or long-press), for the list
// column, W2's panes and W4's profile rows. Frozen after the contracts
// commit. The menu's caption is the session's name.
//   Favorite / Unfavorite        sessionLists.like / unlike
//   Share…                       /s/<name>?via=<me>
//   Duplicate                    ForkDialog (POST /session/fork), then Mine
//   Export as zip / as Ableton Live set
//   Edit (owner)                 /session/<name>?edit=name
//   Report session (others')     ReportDialog
//   Delete (owner)               confirm, POST /deleteSession, then gone
// "Upload session" is app only.
import router from "@/router";
import { apiGet, apiPost } from "@/services/api/client.js";
import sessionLists from "@/stores/sessionLists.js";
import { sessionSheets } from "@/shell/sessionSheets.js";
import { confirm } from "@/shell/confirm.js";
import { toast } from "@/shell/toast.js";
import { isLibrary } from "@/kit/sampleLibrary.js";
import { rowTitle, loopCount } from "@/kit/sessionRow.js";

const API = process.env.VUE_APP_API_URL || "";

export function caption(session) {
  return rowTitle(session);
}

export function shareUrl(session, me) {
  const via = me ? `?via=${encodeURIComponent(me)}` : "";
  return `${window.location.origin}/s/${encodeURIComponent(session.name)}${via}`;
}

export async function share(session, me) {
  const url = shareUrl(session, me);
  const title = `${rowTitle(session)} on cloudloop`;
  try {
    if (navigator.share && document.documentElement.dataset.density === "touch") {
      await navigator.share({ title, url });
      return;
    }
    await navigator.clipboard.writeText(url);
    toast("Session link copied");
  } catch (e) {
    toast("Couldn't copy the link.");
  }
}

// The bundle links mint fresh tokens first: the ones from an earlier load
// go stale as soon as a loop is added.
export async function exportSession(session, kind = "zip") {
  let token = null;
  try {
    const res = await apiGet("/media/session-urls", { session_name: session.name });
    token = res ? (kind === "als" ? res.als_token : res.library_token) : null;
  } catch (e) {
    /* the server gates the download */
  }
  const path = kind === "als" ? "/download/als" : "/download/library";
  let url = `${API}${path}?session_name=${encodeURIComponent(session.name)}`;
  if (token) url += `&token=${encodeURIComponent(token)}`;
  window.location.href = url;
}

export async function deleteSession(session) {
  const title = rowTitle(session);
  const n = loopCount(session);
  const ok = await confirm({
    title: "Delete session?",
    body: `This deletes “${title}” and its ${n} ${n === 1 ? "loop" : "loops"} for everyone. There's no undo.`,
    confirmLabel: "Delete session",
    destructive: true,
  });
  if (!ok) return false;
  toast(`Deleting “${title}”…`);
  try {
    await apiPost("/deleteSession", { session_name: session.name });
    sessionLists.remove(session.name);
    return true;
  } catch (e) {
    toast(`Couldn't delete “${title}”. Try again.`);
    return false;
  }
}

export function menuFor(session, me) {
  if (!session || !session.name) return [];
  const own = !!me && session.creator === me;
  // Your sample library: it stays private and can't go (the server makes
  // it again, empty), so no Share, Duplicate, Favorite or Delete; exporting
  // and renaming it are fine.
  if (isLibrary(session)) {
    return [
      { title: "Export as zip", icon: "mdi-folder-zip-outline", onClick: () => exportSession(session, "zip") },
      { title: "Export as Ableton Live set", icon: "mdi-music-box-outline", onClick: () => exportSession(session, "als") },
      ...(own
        ? [
            { divider: true },
            {
              title: "Edit",
              icon: "mdi-pencil-outline",
              onClick: () => router.push({ path: `/session/${encodeURIComponent(session.name)}`, query: { edit: "name" } }),
            },
          ]
        : []),
    ];
  }
  const liked = sessionLists.isLiked(session.name);
  const items = [];
  if (me) {
    items.push({
      title: liked ? "Unfavorite" : "Favorite",
      icon: liked ? "mdi-heart" : "mdi-heart-outline",
      onClick: () => (liked ? sessionLists.unlike(session.name) : sessionLists.like(session.name)),
    });
  }
  items.push({ title: "Share…", icon: "mdi-export-variant", onClick: () => share(session, me) });
  if (me) items.push({ title: "Duplicate", icon: "mdi-content-copy", onClick: () => sessionSheets.open("fork", session) });
  items.push({ divider: true });
  items.push({ title: "Export as zip", icon: "mdi-folder-zip-outline", onClick: () => exportSession(session, "zip") });
  items.push({ title: "Export as Ableton Live set", icon: "mdi-music-box-outline", onClick: () => exportSession(session, "als") });
  if (own || me) items.push({ divider: true });
  if (own) {
    items.push({
      title: "Edit",
      icon: "mdi-pencil-outline",
      onClick: () => router.push({ path: `/session/${encodeURIComponent(session.name)}`, query: { edit: "name" } }),
    });
  } else if (me) {
    items.push({
      title: "Report session",
      icon: "mdi-flag-outline",
      danger: true,
      onClick: () => sessionSheets.open("report", session),
    });
  }
  if (own) items.push({ title: "Delete", icon: "mdi-trash-can-outline", danger: true, onClick: () => deleteSession(session) });
  return items;
}

export default { menuFor, caption, share, shareUrl, exportSession, deleteSession };
